/**
 * selfServeService.js — the callables behind the self-serve shipping UI.
 *
 *   getRatesUniversal           one server rating call for a self-serve company
 *                               (sell-only rates, each with a rateQuoteId)
 *   getSelfServeAccountSummary  tier, limits, ledger exposure and open invoices
 *                               (the banner and Billing's remaining credit)
 *   requestSelfServeReview      "Request higher limits" (capped per day)
 *
 * The pure request/response shaping lives in src/utils/selfServe.js.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

// The server may run up to its own timeout rating every connected carrier; the
// SDK's 70s default gave up on slow carriers that then answered.
const RATES_TIMEOUT_MS = 120000;

/**
 * Rate on the server. Returns the raw available rates (shape them with
 * toSellOnlyRates) and the carriers that failed, by name.
 * @param {object} request  buildServerRatesRequest(...)
 */
export async function quoteServerRates(request) {
    const call = httpsCallable(getFunctions(), 'getRatesUniversal', { timeout: RATES_TIMEOUT_MS });
    const result = await call(request);
    const data = result?.data?.data || {};
    const errors = Array.isArray(data.carrierResults?.errors) ? data.carrierResults.errors : [];
    return {
        availableRates: Array.isArray(data.availableRates) ? data.availableRates : [],
        failedCarriers: errors.map((e) => ({ name: e.carrier || 'Carrier', error: e.error || 'No rates' })),
    };
}

/**
 * The company's self-serve account: { tier, status, limits, exposure,
 * openInvoices, bookingsToday, bookingEnabled, currency }.
 * @param {string} companyDocId  companies/{id}
 */
export async function fetchSelfServeAccountSummary(companyDocId) {
    const call = httpsCallable(getFunctions(), 'getSelfServeAccountSummary');
    const result = await call({ companyId: companyDocId });
    return result?.data || null;
}

/**
 * Ask staff for a review (higher limits, more carriers, pickups).
 * @param {{reason?: string, context?: string}} p  the refusal that prompted it
 */
export async function requestSelfServeReview({ reason = null, context = null } = {}) {
    const call = httpsCallable(getFunctions(), 'requestSelfServeReview');
    const result = await call({ reason, context });
    return result?.data || { success: true };
}
